import { preload } from 'react-dom'
import Link from 'next/link'

import { Link as MUILink, useTheme } from '@mui/material'

import { BannerBox } from '@/components/Banner/Banner.styled'
import { BannerText } from '@/components/Banner/BannerText'
import { getResponsiveBackgroundImage, getResponsiveImageSrcSet } from '@/helpers/getResponsiveBackgroundImage'
import { getTarget } from '@/helpers/getTarget'
import { resolveSanityImage } from '@/helpers/resolveSanityImage'

import type * as React from 'react'
import type { UnpackedSanityContentBanner } from '@/lib/queries/sanityQueryStrings'

interface BannerProps {
  data: UnpackedSanityContentBanner
  /** True for the slide visible on first paint, so its image gets an early, high-priority preload. */
  priority?: boolean
}

export const Banner: React.FC<BannerProps> = ({ data, priority }) => {
  const theme = useTheme()
  const image = resolveSanityImage(data.image)

  if (priority && image) {
    preload(image.width(theme.breakpoints.values.xl).auto('format').url(), {
      as: 'image',
      fetchPriority: 'high',
      imageSrcSet: getResponsiveImageSrcSet(image, theme),
      imageSizes: '100vw',
    })
  }

  return (
    <BannerBox
      sx={{
        backgroundPosition: { xs: data.contentPlacement === 'right' ? 'top left' : 'top right', xl: 'center' },
        backgroundImage: getResponsiveBackgroundImage(image, theme),
      }}
    >
      {data.bannerLink?.url ? (
        <MUILink
          component={Link}
          href={data.bannerLink?.url}
          target={getTarget(data.bannerLink?.openInNewTab)?.target ?? '_self'}
          sx={{
            display: 'flex',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'none' },
            height: '100%',
            width: '100%',
          }}
        >
          <BannerText data={data} />
        </MUILink>
      ) : (
        <BannerText data={data} />
      )}
    </BannerBox>
  )
}
